import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { PostCard } from "@/components/PostCard";
import { PollCard } from "@/components/PollCard";
import { MemoriesCard } from "@/components/MemoriesCard";
import { StoriesBar } from "@/components/StoriesBar";
import { LiveBanner } from "@/components/LiveBanner";
import { BoostedBanner, BoostedBannerInline } from "@/components/BoostedBanner";
import { NewArtistsBanner } from "@/components/NewArtistsBanner";
import { Newspaper, UserPlus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ArtistCreateFab } from "@/components/ArtistCreateFab";

export default function FeedPage() {
  const { api, user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [polls, setPolls] = useState([]);
  const [memories, setMemories] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchFeed = useCallback(async () => {
    try {
      const [feedRes, pollsRes, memoriesRes] = await Promise.all([
        api.get("/feed", { params: { limit: 30 } }),
        api.get("/polls", { params: { scope: "feed", limit: 10 } }),
        api.get("/feed/memories").catch(() => ({ data: { memories: [] } })),
      ]);
      setPosts(feedRes.data.posts);
      setPolls(pollsRes.data.polls);
      setMemories(memoriesRes.data.memories);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchFeed();
  }, [fetchFeed]);

  const handleLike = async (postId) => {
    const res = await api.post(`/posts/${postId}/like`);
    setPosts((prev) =>
      prev.map((p) =>
        p.id === postId
          ? { ...p, is_liked: res.data.liked, likes_count: res.data.likes_count }
          : p
      )
    );
  };

  const handleReact = async (postId, reactionType) => {
    const res = await api.post(`/posts/${postId}/react`, { reaction_type: reactionType });
    setPosts((prev) =>
      prev.map((p) =>
        p.id === postId
          ? { ...p, user_reaction: res.data.reaction_type, reactions: res.data.reactions }
          : p
      )
    );
    return res.data;
  };

  const handleVotePoll = async (pollId, optionId) => {
    const res = await api.post(`/polls/${pollId}/vote`, { option_id: optionId });
    setPolls((prev) => prev.map((p) => (p.id === pollId ? res.data.poll : p)));
    return res.data.poll;
  };

  // Merge posts and polls into a single timeline
  const feedItems = [
    ...posts.map((p) => ({ ...p, _type: "post" })),
    ...polls.map((p) => ({ ...p, _type: "poll" })),
  ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  return (
    <div className="py-6 space-y-6" data-testid="feed-page">
      <div>
        <h1 className="font-heading font-bold text-2xl text-foreground mb-1">Mon Feed</h1>
        <p className="text-sm text-muted-foreground">Les dernieres publications de tes artistes</p>
      </div>

      {/* Lives en cours */}
      <LiveBanner />

      {/* Stories */}
      <StoriesBar />

      {/* Nouveaux artistes */}
      <NewArtistsBanner />

      {/* Souvenirs */}
      {!loading && memories.length > 0 && (
        <MemoriesCard memories={memories} onLike={handleLike} onReact={handleReact} />
      )}

      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-foreground/5 rounded-3xl h-32 animate-pulse" />
          ))}
        </div>
      ) : feedItems.length === 0 ? (
        <div className="text-center py-20">
          <Newspaper className="w-14 h-14 text-muted-foreground mx-auto mb-4" />
          <h3 className="font-heading font-bold text-lg text-foreground mb-2">Ton feed est vide</h3>
          <p className="text-sm text-muted-foreground mb-6 max-w-xs mx-auto">
            Suis des artistes pour voir leurs publications ici
          </p>
          <button
            onClick={() => navigate("/discover")}
            className="inline-flex items-center gap-2 bg-[#FF6B35] text-white font-bold px-6 py-3 rounded-full shadow-[0_0_20px_rgba(255,107,53,0.4)] hover:scale-105 active:scale-95 transition-transform duration-200"
            data-testid="feed-discover-btn"
          >
            <UserPlus className="w-4 h-4" />
            Explorer des artistes
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Top Ad Placement */}
          <BoostedBanner placement="feed" />

          {feedItems.map((item, index) => (
            <div key={item._type === "poll" ? `poll-${item.id}` : item.id}>
              {item._type === "poll" ? (
                <PollCard poll={item} onVote={handleVotePoll} />
              ) : (
                <PostCard post={item} onLike={handleLike} onReact={handleReact} onDelete={(id) => setPosts((prev) => prev.filter((p) => p.id !== id))} onUpdate={(id, updated) => setPosts((prev) => prev.map((p) => p.id === id ? { ...p, ...updated } : p))} onHide={(id) => setPosts((prev) => prev.filter((p) => p.id !== id))} onBlock={(artistId) => setPosts((prev) => prev.filter((p) => p.artist_id !== artistId))} />
              )}
            </div>
          ))}
        </div>
      )}
      <ArtistCreateFab tab="posts" label="Publier" testId="create-post-fab" />
    </div>
  );
}
